import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@anytag/shared-atoms';
import { useUserRoles } from '@anytag/auth';
import { useDownloadFile } from '@anytag/shared-hooks';
import { QuerySocialAccountType } from '@anytag/graphql';
import { getAdvancedSearchApiUrl } from '@anytag/shared-utils';
import { DownloadFileType, DropdownOption, FormAction } from '@anytag/shared-types';
import { ButtonDropdown, ButtonDropdownProps, DownloadSubmenuIcon } from '@anytag/shared-atoms';
import {
  GoogleAuthDialog,
  useGoogleAuthDownload,
  getDownloadCSVRangeRows,
  DownloadCsvPagesAndRowsModal,
  DownloadCsvPagesAndRowsModalValues
} from '@anytag/shared-molecules';
import { useTrendingInfluencersFilter } from './hooks';

export const ExportButton = (props: Omit<ButtonDropdownProps<DropdownOption>, 'options'>) => {
  const { t } = useTranslation();
  const { isAdminStaff, isPartner } = useUserRoles();
  const { limit, filter } = useTrendingInfluencersFilter();
  const { handleDownloadFile, downloading: csvDownloading } = useDownloadFile();
  const [downloadingType, setDownloadingType] = useState<DownloadFileType | null>(null);
  const { dialogProps, handleTrendingInfluencersSpreadsheetDownload } = useGoogleAuthDownload({
    goBackUrl: '/trending/influencers'
  });

  const downloadOptions: DropdownOption[] = [
    {
      label: t<string>('CSV'),
      value: DownloadFileType.CSV,
      prefixIcon: <DownloadSubmenuIcon icon="csv" />
    },
    ...(isAdminStaff || isPartner
      ? [
          {
            label: t<string>('Spreadsheet'),
            value: DownloadFileType.SPREADSHEET,
            prefixIcon: <DownloadSubmenuIcon icon="spreadsheet" />
          }
        ]
      : [])
  ];

  const handleDownload: FormAction<DownloadCsvPagesAndRowsModalValues>['onSubmit'] = async (formValues) => {
    const { firstRow, lastRow } = getDownloadCSVRangeRows(formValues);

    if (downloadingType === DownloadFileType.CSV) {
      await handleDownloadFile({
        url: getAdvancedSearchApiUrl(`api/trending-influencers/csv`),
        filenamePrefix: `${filter.tab.toLowerCase()}-trending-influencers-${new Date().getTime()}`,
        params: {
          firstRow,
          lastRow,
          socialMedia: filter.tab,
          countryId: filter.countryId,
          categoryIds: filter.categoryIds,
          minFollowers: filter.minFollowers,
          growthPeriod: filter.followerGrowthPeriod
        }
      });
    } else if (downloadingType === DownloadFileType.SPREADSHEET) {
      await handleTrendingInfluencersSpreadsheetDownload({
        lastRow,
        firstRow,
        countryId: filter.countryId,
        minFollowers: Number(filter.minFollowers),
        growthPeriod: filter.followerGrowthPeriod,
        categoryIds: filter.categoryIds.map(Number),
        socialMedia: filter.tab as unknown as QuerySocialAccountType
      });
    }

    setDownloadingType(null);
  };

  return (
    <>
      <Form.Field className="flex-none self-end">
        <ButtonDropdown
          align="end"
          side="bottom"
          variant="solid"
          options={downloadOptions}
          title={t('Button.Export')}
          disabled={csvDownloading}
          prefixIcon={{ icon: 'download', size: '14px' }}
          onSelectOption={(option) => setDownloadingType(option.value as DownloadFileType)}
          {...props}
        />
      </Form.Field>

      <DownloadCsvPagesAndRowsModal
        limit={limit}
        maxRows={300}
        open={!!downloadingType}
        onSubmit={handleDownload}
        downloading={csvDownloading}
        onClose={() => setDownloadingType(null)}
        title={downloadingType === DownloadFileType.SPREADSHEET ? t('Spreadsheet Export') : undefined}
      />

      <GoogleAuthDialog {...dialogProps} />
    </>
  );
};
